import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

export const BackgroundGradientAnimation = ({
  gradientBackgroundStart = "rgb(14, 11, 20)",
  gradientBackgroundEnd = "rgb(20, 15, 32)",
  firstColor = "135, 115, 180",
  secondColor = "90, 70, 102",
  thirdColor = "100, 80, 140",
  fourthColor = "67, 50, 80",
  fifthColor = "156, 139, 194",
  pointerColor = "120, 90, 160",
  size = "72%",
  blendingValue = "hard-light",
  children,
  className,
  interactive = true,
  containerClassName,
}: {
  gradientBackgroundStart?: string;
  gradientBackgroundEnd?: string;
  firstColor?: string;
  secondColor?: string;
  thirdColor?: string;
  fourthColor?: string;
  fifthColor?: string;
  pointerColor?: string;
  size?: string;
  blendingValue?: string;
  children?: React.ReactNode;
  className?: string;
  interactive?: boolean;
  containerClassName?: string;
}) => {
  const interactiveRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const blob1Ref = useRef<HTMLDivElement>(null);
  const blob2Ref = useRef<HTMLDivElement>(null);
  const blob3Ref = useRef<HTMLDivElement>(null);
  const blob4Ref = useRef<HTMLDivElement>(null);
  const blob5Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.setProperty("--gradient-background-start", gradientBackgroundStart);
    document.body.style.setProperty("--gradient-background-end", gradientBackgroundEnd);
    document.body.style.setProperty("--first-color", firstColor);
    document.body.style.setProperty("--second-color", secondColor);
    document.body.style.setProperty("--third-color", thirdColor);
    document.body.style.setProperty("--fourth-color", fourthColor);
    document.body.style.setProperty("--fifth-color", fifthColor);
    document.body.style.setProperty("--pointer-color", pointerColor);
    document.body.style.setProperty("--size", size);
    document.body.style.setProperty("--blending-value", blendingValue);
  }, []);

  useEffect(() => {
    if (!interactive) return;
    const blob = interactiveRef.current;
    const container = containerRef.current;
    if (!blob || !container) return;

    const wrappers = [blob1Ref, blob2Ref, blob3Ref, blob4Ref, blob5Ref]
      .map((r) => r.current)
      .filter((el): el is HTMLDivElement => el !== null);

    // Anchor offsets relative to viewport center — roughly where each ambient
    // blob spends its time orbiting. Used only to compute the repulsion vector.
    const anchors = [
      { x: 0, y: 0 },        // blob 1: center, vertical drift
      { x: -380, y: 0 },     // blob 2: left orbit
      { x: 380, y: 0 },      // blob 3: right orbit
      { x: -200, y: 0 },     // blob 4: mid-left horizontal
      { x: -620, y: 560 },   // blob 5: far bottom-left orbit
    ];
    const phys = anchors.map(() => ({ dx: 0, dy: 0, vx: 0, vy: 0 }));

    // Paint-smear / puddle model: no repulsion (paint doesn't bounce off a
    // finger, it gets dragged), wide drag radius, heavy friction, very soft
    // spring. Tuned for slow, languid motion — blob oozes back over several
    // seconds rather than snapping. Critically damped: friction loss per
    // frame (~0.11) ≈ 2·√SPRING_K so no oscillation.
    const REPEL_RADIUS = 0;
    const REPEL_STRENGTH = 0;
    const DRAG_RADIUS = 500;
    const DRAG_STRENGTH = 0.75;
    const SPRING_K = 0.003;
    const FRICTION = 0.89;
    const MAX_SPEED = 2.5;

    const cur = { x: 0, y: 0 };
    const tgt = { x: 0, y: 0 };
    let prevCurX = 0;
    let prevCurY = 0;
    let raf = 0;

    const onMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      tgt.x = e.clientX - rect.left;
      tgt.y = e.clientY - rect.top;
    };

    const tick = () => {
      // Cursor bubble — smooth, lazy trail (higher divisor = slower)
      cur.x += (tgt.x - cur.x) / 40;
      cur.y += (tgt.y - cur.y) / 40;
      blob.style.transform = `translate(${Math.round(cur.x)}px, ${Math.round(cur.y)}px)`;

      // Cursor velocity this frame — drives the "smear" drag force
      const cvx = cur.x - prevCurX;
      const cvy = cur.y - prevCurY;
      prevCurX = cur.x;
      prevCurY = cur.y;

      const rect = container.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;

      for (let i = 0; i < wrappers.length; i++) {
        const p = phys[i];
        const ax = cx + anchors[i].x + p.dx;
        const ay = cy + anchors[i].y + p.dy;
        const dx = ax - cur.x;
        const dy = ay - cur.y;
        const dist = Math.hypot(dx, dy);

        // Gentle repel from the cursor
        if (dist < REPEL_RADIUS && dist > 0.5) {
          const falloff = 1 - dist / REPEL_RADIUS;
          const push = (REPEL_STRENGTH * falloff * falloff) / dist;
          p.vx += dx * push * 6;
          p.vy += dy * push * 6;
        }

        // Drag along the cursor's motion — the "smear"
        if (dist < DRAG_RADIUS) {
          const drag = (1 - dist / DRAG_RADIUS) * DRAG_STRENGTH;
          p.vx += cvx * drag;
          p.vy += cvy * drag;
        }

        // Spring back to resting anchor
        p.vx += -p.dx * SPRING_K;
        p.vy += -p.dy * SPRING_K;
        p.vx *= FRICTION;
        p.vy *= FRICTION;

        // Clamp velocity so sudden cursor flicks can't fling a blob
        const speed = Math.hypot(p.vx, p.vy);
        if (speed > MAX_SPEED) {
          p.vx = (p.vx / speed) * MAX_SPEED;
          p.vy = (p.vy / speed) * MAX_SPEED;
        }

        p.dx += p.vx;
        p.dy += p.vy;

        wrappers[i].style.transform = `translate(${p.dx.toFixed(1)}px, ${p.dy.toFixed(1)}px)`;
      }

      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [interactive]);

  const [isSafari, setIsSafari] = useState(false);
  useEffect(() => {
    setIsSafari(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        "h-screen w-screen relative overflow-hidden top-0 left-0 bg-[linear-gradient(40deg,var(--gradient-background-start),var(--gradient-background-end))]",
        containerClassName
      )}
    >
      <svg className="hidden">
        <defs>
          <filter id="blurMe">
            <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
              result="goo"
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </svg>

      <div className={cn("", className)}>{children}</div>

      <div
        className={cn(
          "gradients-container h-full w-full blur-lg",
          isSafari ? "blur-2xl" : "[filter:url(#blurMe)_blur(40px)]"
        )}
      >
        {/* Blob 1 — vertical drift */}
        <div ref={blob1Ref} className="absolute inset-0 will-change-transform">
          <div
            className={cn(
              `absolute [background:radial-gradient(circle_at_center,_rgba(var(--first-color),_0.45)_0,_rgba(var(--first-color),_0)_50%)_no-repeat]`,
              `[mix-blend-mode:var(--blending-value)] w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]`,
              `[transform-origin:center_center]`,
              `animate-first`,
              `opacity-100`
            )}
          />
        </div>
        {/* Blob 2 — circle reverse */}
        <div ref={blob2Ref} className="absolute inset-0 will-change-transform">
          <div
            className={cn(
              `absolute [background:radial-gradient(circle_at_center,_rgba(var(--second-color),_0.4)_0,_rgba(var(--second-color),_0)_50%)_no-repeat]`,
              `[mix-blend-mode:var(--blending-value)] w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]`,
              `[transform-origin:calc(50%-400px)]`,
              `animate-second`,
              `opacity-100`
            )}
          />
        </div>
        {/* Blob 3 — circle forward */}
        <div ref={blob3Ref} className="absolute inset-0 will-change-transform">
          <div
            className={cn(
              `absolute [background:radial-gradient(circle_at_center,_rgba(var(--third-color),_0.42)_0,_rgba(var(--third-color),_0)_50%)_no-repeat]`,
              `[mix-blend-mode:var(--blending-value)] w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]`,
              `[transform-origin:calc(50%+400px)]`,
              `animate-third`,
              `opacity-100`
            )}
          />
        </div>
        {/* Blob 4 — horizontal */}
        <div ref={blob4Ref} className="absolute inset-0 will-change-transform">
          <div
            className={cn(
              `absolute [background:radial-gradient(circle_at_center,_rgba(var(--fourth-color),_0.38)_0,_rgba(var(--fourth-color),_0)_50%)_no-repeat]`,
              `[mix-blend-mode:var(--blending-value)] w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]`,
              `[transform-origin:calc(50%-200px)]`,
              `animate-fourth`,
              `opacity-70`
            )}
          />
        </div>
        {/* Blob 5 — wide circle */}
        <div ref={blob5Ref} className="absolute inset-0 will-change-transform">
          <div
            className={cn(
              `absolute [background:radial-gradient(circle_at_center,_rgba(var(--fifth-color),_0.35)_0,_rgba(var(--fifth-color),_0)_50%)_no-repeat]`,
              `[mix-blend-mode:var(--blending-value)] w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]`,
              `[transform-origin:calc(50%-800px)_calc(50%+800px)]`,
              `animate-fifth`,
              `opacity-100`
            )}
          />
        </div>

        {/* Mouse-interactive blob — follows cursor, merges with ambient blobs via goo filter */}
        {interactive && (
          <div
            ref={interactiveRef}
            className={cn(
              `absolute [background:radial-gradient(circle_at_center,_rgba(var(--pointer-color),_0.6)_0,_rgba(var(--pointer-color),_0)_50%)_no-repeat]`,
              `[mix-blend-mode:var(--blending-value)] w-[320px] h-[320px] top-[-160px] left-[-160px]`,
              `opacity-90 pointer-events-none`
            )}
          />
        )}
      </div>
    </div>
  );
};
